import React, { useState, useRef } from 'react';
import { X, Upload, Download, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const CsvImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [preview, setPreview] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  // Função para download do template
  const downloadTemplate = () => {
    const headers = [
      'serial_number',
      'brand',
      'model',
      'size',
      'category',
      'tire_type',
      'purchase_date',
      'purchase_price',
      'expected_lifespan_km',
      'current_tread_depth_mm',
      'warehouse_location',
      'notes'
    ];

    const exampleData = [
      [
        'BR001234567',
        'Michelin',
        'XZE 2+',
        '295/80R22.5',
        'drive',
        'new',
        '2024-01-15',
        '2850.00',
        '120000',
        '16.5',
        'Depósito A',
        'Pneu novo para caminhão'
      ],
      [
        'BR001234568',
        'Goodyear',
        'G647 RSS',
        '295/80R22.5',
        'steer',
        'new',
        '2024-01-15',
        '2650.00',
        '100000',
        '16.0',
        'Depósito A',
        ''
      ],
      [
        'BR001234569',
        'Michelin',
        'XZA 3+',
        '275/80R22.5',
        'trailer',
        'new',
        '2024-01-15',
        '2750.00',
        '110000',
        '15.5',
        'Depósito B',
        ''
      ]
    ];

    const csvContent = [
      headers.join(','),
      ...exampleData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'template_pneus.csv';
    link.click();

    toast.success('Template baixado com sucesso!');
  };

  // Processar arquivo CSV
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Por favor, selecione um arquivo CSV');
      return;
    }

    setFile(selectedFile);
    readCsvFile(selectedFile);
  };

  const readCsvFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').map(line => line.trim()).filter(line => line);

      if (lines.length < 2) {
        toast.error('Arquivo CSV vazio ou inválido');
        return;
      }

      // Primeira linha é o header
      const headers = lines[0].split(',').map(h => h.trim());

      // Validar headers obrigatórios
      const requiredHeaders = ['serial_number', 'brand', 'model', 'size'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

      if (missingHeaders.length > 0) {
        toast.error(`Colunas obrigatórias faltando: ${missingHeaders.join(', ')}`);
        return;
      }

      // Parsear dados
      const data = [];
      const errors = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = {};

        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });

        // Validar linha
        const lineErrors = [];
        if (!row.serial_number) lineErrors.push('Nº série obrigatório');
        if (!row.brand) lineErrors.push('Marca obrigatória');
        if (!row.model) lineErrors.push('Modelo obrigatório');
        if (!row.size) lineErrors.push('Tamanho obrigatório');

        if (lineErrors.length > 0) {
          errors.push({
            line: i + 1,
            serial: row.serial_number || `Linha ${i + 1}`,
            errors: lineErrors
          });
        }

        data.push(row);
      }

      setCsvData(data);
      setPreview(data.slice(0, 10));
      setValidationErrors(errors);

      if (errors.length > 0) {
        toast.error(`${errors.length} linhas com erro de validação`);
      } else {
        toast.success(`${data.length} linhas prontas para importar`);
      }
    };

    reader.onerror = () => {
      toast.error('Erro ao ler arquivo');
    };

    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (csvData.length === 0) {
      toast.error('Nenhum dado para importar');
      return;
    }

    if (validationErrors.length > 0) {
      toast.error('Corrija os erros antes de importar');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/api/tires/import-csv', { data: csvData });

      const { success, failed, total } = response.data.data;

      if (failed > 0) {
        toast.error(`${success} pneus importados, ${failed} com erro`);
        console.log('Erros:', response.data.data.errors);
      } else {
        toast.success(`${success} pneus importados com sucesso!`);
      }

      if (success > 0) {
        onSuccess();
        resetForm();
        onClose();
      }
    } catch (error) {
      console.error('Erro ao importar CSV:', error);
      toast.error(error.response?.data?.message || 'Erro ao importar CSV');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setCsvData([]);
    setPreview([]);
    setValidationErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Importar Pneus via CSV
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Template Download */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                  Baixe o modelo CSV
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Use o template com exemplos para facilitar a importação
                </p>
              </div>
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Download className="w-4 h-4" />
                Baixar Template
              </button>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Selecione o arquivo CSV
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="w-12 h-12 text-gray-400 mb-3" />
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Clique para selecionar ou arraste o arquivo
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Apenas arquivos .csv (máx. 100 linhas)
                </p>
              </label>
            </div>
            {file && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                Arquivo selecionado: {file.name}
              </p>
            )}
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-red-900 dark:text-red-200 mb-2">
                    {validationErrors.length} erro(s) encontrado(s)
                  </h3>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {validationErrors.map((err, idx) => (
                      <div key={idx} className="text-sm text-red-700 dark:text-red-300">
                        Linha {err.line} ({err.serial}): {err.errors.join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Preview */}
          {preview.length > 0 && validationErrors.length === 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Preview ({csvData.length} pneus prontos)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Nº Série</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Marca</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Modelo</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Tamanho</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300">Categoria</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {preview.map((row, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white font-mono">{row.serial_number}</td>
                        <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{row.brand}</td>
                        <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{row.model}</td>
                        <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{row.size}</td>
                        <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{row.category}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {csvData.length > 10 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
                    ... e mais {csvData.length - 10} linhas
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleImport}
            disabled={loading || csvData.length === 0 || validationErrors.length > 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? 'Importando...' : `Importar ${csvData.length} Pneus`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CsvImportModal;
